// src/utils/sortUtils.ts
export type SortDir = "asc" | "desc";
export type SortKey = "date" | "invoice";

const collator = new Intl.Collator(undefined, { sensitivity: "base", numeric: true });

/** Extracts trailing numeric part and prefix for invoice-like strings.
 *  INV-00012 -> { prefix: "INV-", num: 12 }
 *  S-9       -> { prefix: "S-", num: 9 }
 *  "ABC"     -> { prefix: "ABC", num: NaN }
 */
export function parseInvoiceNo(no?: string | null): { prefix: string; num: number } {
  const s = (no ?? "").trim();
  if (!s) return { prefix: "", num: NaN };
  const m = s.match(/(\d+)(?!.*\d)/); // last number
  if (!m) return { prefix: s, num: NaN };
  const num = Number(m[1]);
  const prefix = s.slice(0, m.index!);
  return { prefix, num };
}

export function compareDates(a?: string, b?: string, dir: SortDir = "desc"): number {
  const da = a ? new Date(a).getTime() : 0;
  const db = b ? new Date(b).getTime() : 0;
  return dir === "asc" ? da - db : db - da;
}

/** Smart invoice comparator:
 * - If both have numeric tails and same prefix -> numeric compare
 * - Else -> natural string compare as fallback
 * - Null/empty invoice_no pushed last
 */
export function compareInvoiceNo(a?: string | null, b?: string | null, dir: SortDir = "desc"): number {
  const aa = (a ?? "").trim();
  const bb = (b ?? "").trim();
  if (!aa && !bb) return 0;
  if (!aa) return 1; // empty last
  if (!bb) return -1;

  const pa = parseInvoiceNo(aa);
  const pb = parseInvoiceNo(bb);

  if (isFinite(pa.num) && isFinite(pb.num) && pa.prefix.toLowerCase() === pb.prefix.toLowerCase()) {
    return dir === "asc" ? pa.num - pb.num : pb.num - pa.num;
  }

  const s = collator.compare(aa, bb);
  return dir === "asc" ? s : -s;
}

/** Tie-breaker you can reuse everywhere: date DESC then id ASC for stability */
export function tieBreakByDateThenId(
  dateA?: string,
  dateB?: string,
  idA?: number,
  idB?: number
): number {
  const d = compareDates(dateA, dateB, "desc");
  if (d !== 0) return d;
  return (idA ?? 0) - (idB ?? 0);
}
